// Maquette de la coquille (decision de Louka du 01/10) : le chat de Dust porte la coquille commune aux
// trois apps (barre a onglets + rectangle arrondi) et affiche les pages de l'app DANS le rectangle, par
// un cadre de meme origine (front/components/compta/CadreDeLApp.tsx). L'app reste maitresse de ses
// pages : elle annonce la page affichee, le chat lui demande d'en changer. Ce module pur decide quelles
// adresses sont admises et lit ou ecrit ces messages.

const SOURCE_APP = "pirouette-app";
const SOURCE_CHAT = "pirouette-chat";
const TYPE_PAGE = "pirouette:page";
const TYPE_ALLER = "pirouette:aller";
const TYPE_OUVRIR = "pirouette:ouvrir";

// Meme forme que la route admise par l'app (app/lib/messages-chat.ts) : un chemin relatif, au plus une
// requete simple ; ni fragment, ni chevrons, ni guillemets.
const ROUTE = /^\/(?!\/)[A-Za-z0-9_\-/]*(?:\?[A-Za-z0-9_\-=&%.]*)?$/;

function routeValide(v: unknown): v is string {
  return typeof v === "string" && ROUTE.test(v) && !v.includes("..");
}

function sousChemin(chemin: string, racine: string): boolean {
  const p = chemin.split("?")[0];
  return p === racine || p.startsWith(`${racine}/`);
}

/**
 * Une page de l'app affichable dans le rectangle, ou null. Jamais le chat lui-meme, sa porte, ni la
 * page Assistant de l'app : ce serait la coquille dans la coquille.
 */
export function cheminDeLApp(v: unknown): string | null {
  if (!routeValide(v)) {
    return null;
  }
  if (
    sousChemin(v, "/chat") ||
    sousChemin(v, "/api") ||
    sousChemin(v, "/assistant")
  ) {
    return null;
  }
  return v;
}

export function routeDeLaPageDeLApp(
  workspaceId: string,
  chemin: string
): string {
  return `/w/${workspaceId}/app?chemin=${encodeURIComponent(chemin)}`;
}

/** L'entree d'adresse `adresse` correspond a la page affichee (sous-pages comprises). */
export function estActive(
  adresse: string | undefined,
  cheminCourant: string | null
): boolean {
  if (!adresse || !cheminCourant) {
    return false;
  }
  return sousChemin(cheminCourant, adresse.split("?")[0]);
}

/** La page annoncee par l'app du cadre, ou null (autre origine, autre fenetre, autre forme). */
export function pageDuMessage(
  event: MessageEvent,
  cadre: Window | null | undefined,
  origine: string
): string | null {
  if (!cadre || event.origin !== origine || event.source !== cadre) {
    return null;
  }
  const d = event.data as {
    source?: unknown;
    type?: unknown;
    chemin?: unknown;
  } | null;
  if (typeof d !== "object" || d === null) {
    return null;
  }
  if (d.source !== SOURCE_APP || d.type !== TYPE_PAGE) {
    return null;
  }
  return routeValide(d.chemin) ? d.chemin : null;
}

export function messageAller(chemin: string): {
  source: typeof SOURCE_CHAT;
  type: typeof TYPE_ALLER;
  chemin: string;
} {
  return { source: SOURCE_CHAT, type: TYPE_ALLER, chemin };
}

/** L'espace de travail d'une route du chat (/w/<espace>/...), ou null. */
export function espaceDeLaRoute(route: string): string | null {
  const m = /^\/w\/([^/?#]+)/.exec(route);
  return m ? m[1] : null;
}

/**
 * La page de l'app que l'app (fenetre parente) demande d'ouvrir dans le rectangle : lien de citation,
 * entree de sa propre navigation. Null sinon.
 */
export function ouvrirDuMessage(
  event: MessageEvent,
  parent: Window | null | undefined,
  origine: string
): string | null {
  if (!parent || event.origin !== origine || event.source !== parent) {
    return null;
  }
  const d = event.data as {
    source?: unknown;
    type?: unknown;
    chemin?: unknown;
  } | null;
  if (typeof d !== "object" || d === null) {
    return null;
  }
  if (d.source !== SOURCE_APP || d.type !== TYPE_OUVRIR) {
    return null;
  }
  return cheminDeLApp(d.chemin);
}

// Les pages du chat que l'app peut demander d'ouvrir au chargement (?chat=...), relatives a l'espace
// de travail : l'espace commun par categorie, une page d'espace, les pages des assistants.
const ROUTE_DU_CHAT =
  /^(espace-commun\/(folder|website)|spaces\/vlt_[A-Za-z0-9]+(\/categories\/[a-z_]+(\/data_source_views\/dsv_[A-Za-z0-9]+)?)?|builder\/(agents|skills))$/;

export function routeDuChat(valeur: unknown, espace: string): string | null {
  if (typeof valeur !== "string" || !ROUTE_DU_CHAT.test(valeur)) {
    return null;
  }
  return `/w/${espace}/${valeur}`;
}
